import { useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { ModeratorAudienceState, ModeratorState, RoomThread } from "../../shared/types.ts";
import { useRoleAuth } from "../auth/AuthGate.tsx";
import { SignOutButton } from "../auth/SignOutButton.tsx";
import { groupByCategory, isOnAir, sortThreads, splitSections } from "../lib/threads.ts";
import { useProjectorActions, useThreadActions } from "../lib/useThreadActions.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { OnScreenStatus, ProjectorControls, currentProjectedThread } from "../components/OnScreen.tsx";
import { AudienceRecapPanel } from "../components/AudienceRecapPanel.tsx";
import { SortControls, type ThreadSort } from "../components/SortControls.tsx";
import { ThreadCard } from "../components/ThreadCard.tsx";
import { buildAdminPath, buildJoinPath, buildProjectorPath } from "../lib/join.ts";
import { useStaffSessionRoute } from "../lib/useStaffSessionRoute.ts";
import { AudiencePrivacyCue } from "../components/AudiencePrivacyCue.tsx";

/** Moderator works the same unified board as the admin for live threads, while
 *  the right rail is reserved for panel summaries. Transcript is opt-in
 *  (admin toggle) and collapsed by default. */
export function ModeratorView() {
  const auth = useRoleAuth("moderator");
  const sessionRoute = useStaffSessionRoute();
  const moderatorArgs = sessionRoute.hasJoinCodeRoute
    ? sessionRoute.sessionId ? { auth, sessionId: sessionRoute.sessionId } : "skip"
    : { auth };
  const mod = useQuery(convexApi.sessions.getModeratorState, moderatorArgs) as ModeratorState | null | undefined;
  const audience = useQuery(convexApi.audience.getStaffAudienceState, moderatorArgs) as ModeratorAudienceState | null | undefined;
  const actions = useThreadActions(auth, audience ?? null);
  const projectorActions = useProjectorActions(auth, audience ?? null);
  const [sort, setSort] = useState<ThreadSort>("most-upvotes");
  const [byCategory, setByCategory] = useState(false);
  const connected = mod !== undefined;

  const sorted = useMemo(() => sortThreads(audience?.threads ?? [], sort), [audience?.threads, sort]);
  const { live, answered } = useMemo(() => splitSections(sorted), [sorted]);
  // A projected question is pinned inside its own status section so answered
  // material never jumps above the live board.
  const projectedThreadId = audience?.projector?.mode === "thread" ? audience.projector.projectedThreadId : null;
  const pinnedLive = projectedThreadId ? live.find((t) => t.id === projectedThreadId) ?? null : null;
  const liveBoard = pinnedLive ? live.filter((t) => t.id !== pinnedLive.id) : live;
  const pinnedAnswered = projectedThreadId
    ? answered.find((t) => t.id === projectedThreadId) ?? null
    : null;
  const answeredBoard = pinnedAnswered
    ? answered.filter((t) => t.id !== pinnedAnswered.id)
    : answered;

  const joinCode = audience?.joinCode ?? sessionRoute.joinCode;
  const adminHref = buildAdminPath(joinCode);
  const projectorHref = buildProjectorPath(joinCode);

  const card = (t: RoomThread, queueVariant = false) => (
    <ThreadCard
      key={t.id}
      thread={t}
      categories={audience?.categories ?? []}
      onAir={isOnAir(t, audience?.projector)}
      actions={actions}
      queueVariant={queueVariant}
    />
  );

  const threadCollection = (threads: RoomThread[], keyPrefix: string) => byCategory
    ? groupByCategory(threads, audience?.categories ?? []).map((group) => (
        <div key={`${keyPrefix}-${group.category?.id ?? "none"}`}>
          <div className="sub-head" style={{ marginTop: 8 }}>
            {group.category?.label ?? "uncategorised"} · {group.threads.length}
          </div>
          {group.threads.map((thread) => card(thread))}
        </div>
      ))
    : threads.map((thread) => card(thread));

  if (sessionRoute.resolving) {
    return <div className="moderator"><div className="loading">Loading session...</div></div>;
  }

  if (sessionRoute.notFound) {
    return (
      <div className="moderator">
        <header className="topbar">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true" />
            <div className="brand-text">
              <strong title="Conversation Moderator">COMODO</strong>
              <span className="role-badge role-moderator" data-testid="role-badge">Moderator View</span>
            </div>
          </div>
          <div className="top-actions">
            <SignOutButton redirectTo="/moderator" />
          </div>
        </header>
        <main className="participant-join">
          <section className="panel join-panel" data-testid="staff-session-unavailable">
            <h1>Session not found</h1>
            <p>Check the session join code.</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="moderator">
      <header className="topbar mod-top">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-moderator" data-testid="role-badge">Moderator View</span>
          </div>
        </div>
        <div className="top-meta">
          {audience && <AudiencePrivacyCue policy={audience.policy} />}
          <span className={`pill ${connected ? "on" : "off"}`} data-testid="mod-conn">{connected ? "live" : "reconnecting…"}</span>
          <a className="btn link ghost sm" data-testid="open-projector" href={projectorHref} target="_blank" rel="noreferrer">Projector ↗</a>
          <a className="btn link ghost sm" data-testid="open-participants" href={joinCode ? buildJoinPath(joinCode) : "/join"} target="_blank" rel="noreferrer">Participants ↗</a>
          <a className="btn link ghost sm" data-testid="open-admin" href={adminHref} target="_blank" rel="noreferrer">Admin View ↗</a>
          <SignOutButton redirectTo="/moderator" />
        </div>
      </header>

      <div className="mod-body">
        <div className="mod-primary-shell">
          <div className="mod-projector-strip">
            {audience && (
              <section className="onscreen onscreen-bar" data-testid="mod-onscreen">
                <OnScreenStatus
                  projector={audience.projector}
                  threads={audience.threads}
                  artifacts={audience.artifacts}
                />
                <ProjectorControls
                  projector={audience.projector}
                  current={currentProjectedThread(audience.projector, audience.threads)}
                  actions={projectorActions}
                />
              </section>
            )}
          </div>
          <div className="modv7-main">
            <div className="col">
              <section className="panel mod-board-panel" data-testid="audience-panel">
                <div className="panel-head">
                  <h2>Live board <span className="count" data-testid="board-count">{live.length}</span></h2>
                  <SortControls sort={sort} onSort={setSort} byCategory={byCategory} onByCategory={setByCategory} />
                </div>
                <div className="mod-board-scroll">
                  {audience === undefined && <EmptyState>Loading threads…</EmptyState>}
                  {audience === null && <EmptyState>No active session.</EmptyState>}
                  {audience && live.length === 0 && <EmptyState size="sm" testid="board-empty">No live threads yet.</EmptyState>}
                  {audience && pinnedLive && card(pinnedLive)}
                  {audience && threadCollection(liveBoard, "live")}
                  {audience && (
                    <section className="thread-status-section" data-testid="moderator-answered-section">
                      <div className="thread-section-heading">
                        <span>Answered</span>
                        <span className="count">{answered.length}</span>
                      </div>
                      {answered.length === 0
                        ? <EmptyState size="sm">No answered threads yet.</EmptyState>
                        : (
                            <>
                              {pinnedAnswered && card(pinnedAnswered)}
                              {threadCollection(answeredBoard, "answered")}
                            </>
                          )}
                    </section>
                  )}
                </div>
              </section>
            </div>
            {audience && (
              <section className="panel mod-summary-panel" data-testid="moderator-summary-panel">
                <div className="panel-head">
                  <h2>Panel summaries</h2>
                </div>
                <AudienceRecapPanel
                  sessionId={audience.sessionId}
                  recaps={audience.recaps}
                />
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
